import { type BranchKey, STANDARD_BRANCH } from '@festina-life/api'
import { notEmpty } from '@festina-life/core'

import { useBaseBranchWorkspaces } from '../../hooks/useBaseBranchWorkspaces'
import { useIsPersonaEditor } from '../../uiModels/useIsPersonaEditor'
import { isEqualWorkspace } from '../../workspace/helpers'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { ServerDetailsPanel } from '../serverDetails/ServerDetailsPanel'
import { WorkspaceDetailsPanel } from '../workspacesPanel/workspaceDetails/WorkspaceDetailsPanel'
import { BranchDetailsPanel } from './branchDetails/BranchDetailsPanel'
import { conciseWorkspaceToWorkspaceKey } from './branchDetails/branchWorkspaces/helpers'
import {
  isBranchTreeElementBranchKey,
  isBranchTreeElementConfigurableServer,
  isBranchTreeElementWorkspaceKey,
} from './branchesTree/helpers'
import type { BranchTreeElementKey } from './types'
import { UnifiedBranchWorkspaceDetailsPanel } from './UnifiedBranchWorkspaceDetailsPanel'

type Props = {
  activeElementKey: BranchTreeElementKey
  onClose: () => void
}

export const BranchesPanelDetailsSelector = ({
  activeElementKey,
  onClose,
}: Props) => {
  const isPersonaEditor = useIsPersonaEditor()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()

  const isBranchKey = isBranchTreeElementBranchKey(activeElementKey)
  const isWorkspaceKey = isBranchTreeElementWorkspaceKey(activeElementKey)
  const isServer = isBranchTreeElementConfigurableServer(activeElementKey)

  let branchKey: BranchKey | undefined
  if (isBranchKey) {
    branchKey = activeElementKey
  } else if (isWorkspaceKey) {
    branchKey = activeElementKey.branchKey
  }

  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const isUnifiedWorkspaceBranch =
    isPersonaEditor &&
    notEmpty(branchKey) &&
    branchWorkspaces?.length <= 1 &&
    branchKey.name !== STANDARD_BRANCH

  const showUnifiedPanel =
    (isBranchKey || isWorkspaceKey) && isUnifiedWorkspaceBranch
  const showBranchDetailsPanel = isBranchKey && !isUnifiedWorkspaceBranch
  const showWorkspaceDetailsPanel =
    isWorkspaceKey && isPersonaEditor && !isUnifiedWorkspaceBranch
  const showServerDetailsPanel = isServer && isPersonaEditor

  if (showUnifiedPanel && branchKey) {
    const workspace = branchWorkspaces.at(0)
    const unifiedWorkspaceKey = workspace
      ? conciseWorkspaceToWorkspaceKey({
          server: branchKey.server,
          workspace,
        })
      : undefined
    const isWorkspaceOpen =
      currentWorkspaceKey &&
      isEqualWorkspace(unifiedWorkspaceKey, currentWorkspaceKey)

    return (
      <UnifiedBranchWorkspaceDetailsPanel
        workspaceKey={isWorkspaceOpen ? unifiedWorkspaceKey : undefined}
        branchKey={branchKey}
        onClose={onClose}
      />
    )
  } else if (showBranchDetailsPanel) {
    return <BranchDetailsPanel branchKey={activeElementKey} onClose={onClose} />
  } else if (showWorkspaceDetailsPanel) {
    return (
      <WorkspaceDetailsPanel
        workspaceKey={activeElementKey}
        onClose={onClose}
      />
    )
  } else if (showServerDetailsPanel) {
    return <ServerDetailsPanel server={activeElementKey} onClose={onClose} />
  }

  return null
}
